'use client';

import { useEffect, useMemo, useState } from 'react';

import { useTranslations } from 'next-intl';

import ReferralLink from '@/components/global/ReferralLink';
import { Image } from '@/components/shared/Image';
import { H2, P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/navigation';
import { getPresaleEndMs } from '@/lib/utils/getPresaleEndMs';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';

import type { T_Pool } from '@/ts/types/T_Pool';

interface I_EmptyState {
  title: string;
  text: string;
  actionText: string;
}

interface I_NoDataWrapperProps {
  emptyState: I_EmptyState;
  variant: string;
}

function NoDataWrapper({ emptyState, variant }: I_NoDataWrapperProps) {
  const t = useTranslations();
  const isReferrals = variant === 'referrals';
  const isPredictions = variant === 'predictions';
  const isPools = variant === 'pools';
  const { title, text, actionText } = emptyState;

  // Get pools from GlobalDataProvider (no additional API call)
  const poolsFromContext = useGlobalData('pools');
  const pools = useMemo<T_Pool[]>(
    () => poolsFromContext || [],
    [poolsFromContext]
  );
  const [linkHref, setLinkHref] = useState<string | null>(null);
  const isLoadingLink = false;

  // Check if pools are still loading from context
  const isPoolsLoading =
    poolsFromContext === null || poolsFromContext === undefined;

  // For pools: Check if any pool has active presale (presaleEndDate hasn't ended)
  const hasActivePresale = useMemo(() => {
    if (!isPools || pools.length === 0) return false;

    return pools.some(pool => {
      if (!pool.presaleEndDate) return false;
      const presaleEndMs = getPresaleEndMs(pool.presaleEndDate);
      if (presaleEndMs === null) return false;
      // Presale is active if end date is in the future
      return Date.now() < presaleEndMs;
    });
  }, [isPools, pools]);

  // For pools: Get first pool for navigation
  const firstPool = useMemo(() => {
    return pools.length > 0 ? pools[0] : null;
  }, [pools]);

  // Determine link href for pools (synchronous)
  const poolsLinkHref = useMemo(() => {
    if (!isPools || pools.length === 0) return null;

    // Check if there's at least one pool with active presale
    if (hasActivePresale) {
      return C_Routes[E_Routes.POOLS_PRESALE].link;
    }
    // Otherwise, navigate to pools page with first pool's slug
    if (firstPool?.slug) {
      return `/pools/${firstPool.slug}`;
    }

    return null;
  }, [isPools, pools, hasActivePresale, firstPool]);

  // Set pools link immediately (synchronous)
  useEffect(() => {
    if (isPools && poolsLinkHref && !linkHref) {
      setLinkHref(poolsLinkHref);
    }
  }, [isPools, poolsLinkHref, linkHref]);

  return (
    <div>
      {isReferrals && (
        <div className="w-full flex items-center justify-center mb-5 relative ">
          <Image
            src="/assets/images/account/emoji.png"
            alt="Emoji"
            width={64}
            height={64}
            className="w-16 self-center"
          />
        </div>
      )}
      <H2 className="justify-center mb-3">{title}</H2>
      <P className="justify-center mb-7 max-w-125 text-center">{text}</P>
      {isReferrals ? (
        <>
          <P className="justify-center mb-7 max-w-125 text-center">
            {t('profile.sections.referrals.emptyState.rewardText')}
          </P>
          <ReferralLink />
        </>
      ) : isPredictions ? null : linkHref ? (
        <Button className="w-full h-10" asChild>
          <Link href={linkHref}>{actionText}</Link>
        </Button>
      ) : (
        <Button
          className="w-full h-10"
          disabled={isLoadingLink || isPoolsLoading}
        >
          {actionText}
        </Button>
      )}
    </div>
  );
}

export default NoDataWrapper;
